import React from 'react';
import styled, { css, SimpleInterpolation } from 'styled-components';
import { boxShadow } from '../../../commonStyles/boxStyles';
import { Image as ImageType } from '../../../commonTypes/image';
import colors from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing, { spacingWithBorder } from '../../../helpers/spacing';
import Icon from '../../Icon';
import Image from '../../Image';
import Link from '../../Link';

const transformTransition = 'transform 0.3s ease-out';
export const cardBorderPx = 1;

const imageStyleIEFallback = (
  url: string,
  objectFit: 'contain' | 'cover',
  objectPosition: React.CSSProperties['objectPosition']
): SimpleInterpolation =>
  css`
    display: none;

    @media screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      display: block;
      background-image: url(${url});
      background-size: ${objectFit};
      background-position: ${objectPosition || 'initial'};
      width: 100%;
      height: 100%;
    }
  `;

export const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: 195px;

  border-radius: ${spacingWithBorder(1, cardBorderPx)}
    ${spacingWithBorder(1, cardBorderPx)} 0 0;

  ${mediaQuery.tabletPortrait`
    height: 205px;
  `}

  ${mediaQuery.tabletLandscape`
    height: 150px;
  `}
`;

export const StyledImage = styled(Image)`
  height: 100%;
  width: 100%;
  object-position: 50% 50%;
  object-fit: cover;

  border-radius: 0;

  @media screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    display: none;
  }
`;

export const ImageIEFallback = styled.div<ImageType>`
  ${({ url }) => imageStyleIEFallback(url, 'cover', '50% 50%')}
`;

export const ContentWrapper = styled.div`
  /* fill up the card container to push the read time to the bottom */
  margin-bottom: auto;
  padding: ${spacing(3)} ${spacingWithBorder(3, cardBorderPx)};

  /* If no image */
  &:first-child {
    padding-top: ${spacingWithBorder(3, cardBorderPx)};
    margin-top: ${spacing(1)};

    /* vertically centre the content */
    ${mediaQuery.tabletPortrait`
      margin-top: auto;
    `}
  }

  /* If no read time */
  &:last-child {
    padding-bottom: ${spacingWithBorder(3, cardBorderPx)};
    margin-bottom: ${spacing(1)};

    ${mediaQuery.tabletPortrait`
      margin-bottom: auto;
    `}
  }

  text-align: center;
`;

export const Heading = styled.h3`
  ${fonts.headingSmall}
  text-align: center;
  margin: 0;

  /* Restrict max height to two lines to ensure that the animation works properly. 
     Otherwise, the animation will be based on the pre-truncated text height which
     may be longer than two lines and it will animate until that height. However, 
     the text will be truncated by the time the animation completes, meaning that 
     the animation height will be greater than the actual height causing a "jump" 
     at the end of the animation.
     
     We calculate this as 2x lineheight where the lineheight is taken from the 
     Morethan theme */
  max-height: ${spacing(8)};
`;

export const Subheading = styled.p`
  ${fonts.paragraphSmall};
  margin: ${spacing(2)} 0 0;
  & {
    color: ${colors.core01};
  }
`;

export const Body = styled.p<{ maxLines?: number }>`
  ${fonts.paragraph}
  margin: ${spacing(2)} 0 0;

  /* See comment on Heading style above */
  max-height: ${({ maxLines }) =>
    maxLines ? `calc(${spacing(3.5)} * maxLines)` : spacing(7)};
  ${mediaQuery.tabletLandscape`
    max-height: ${({ maxLines }) =>
      maxLines ? `calc(${spacing(3)} * maxLines)` : spacing(6)};
  `}
`;

export const ReadMoreButton = styled(Link)`
  ${fonts.paragraph};
  margin: ${spacing(2)} ${spacingWithBorder(3, cardBorderPx)} ${spacing(3)};
  display: flex;
  align-items: center;
`;

export const ReadMoreIcon = styled(Icon)`
  margin-left: ${spacing(1.25)};
`;

export const ReadTimeWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export const ReadTime = styled.span`
  ${fonts.paragraphSmall}
  letter-spacing: 1.5px;
  & {
    color: ${colors.neutral02};
  }
`;

export const ReadTimeIcon = styled(Icon)`
  margin-right: ${spacing(1)};
`;

export type RelatedContentCardContainerProps = {
  disabled?: boolean;
  displayBackground?: boolean;
};

const nonDisabledLinkStyles = css<RelatedContentCardContainerProps>`
  &:link, /* to override IE's automatic blue link styles */
  &:visited {
    color: inherit;
  }
  &:hover,
  &:focus-visible {
    box-shadow: ${boxShadow};

    transform: translateY(-${spacing(1)});
  }

  &:focus-visible {
    border-color: ${colors.core02};
  }

  &:active {
    transform: none;
    border-color: ${colors.core02};
    box-shadow: inset -2px 2px 4px 2px rgba(0, 0, 0, 0.12);
  }

  &:hover,
  &:focus-visible,
  &:active {
    ${Heading} {
      color: ${colors.core01};
    }
  }
`;

const disabledLinkStyles = css`
  cursor: not-allowed;
  border-color: ${colors.neutral01};
  opacity: 0.4;
`;

export const RelatedContentCardContainer = styled.a<RelatedContentCardContainerProps>`
  transition: ${transformTransition};
  /* Spacing */
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  background: ${colors.neutral08};

  margin: ${spacing(1)} 0;

  ${mediaQuery.tabletLandscape`
    margin: ${spacing(2)} 0;
  `}

  border: ${cardBorderPx}px solid ${colors.cardBorder};
  border-radius: ${({ theme }) => `${theme.cardRadius}`};

  /* Link styling */
  text-decoration: none;
  outline: none;

  ${({ disabled }) => !disabled && nonDisabledLinkStyles}
  ${({ disabled }) => disabled && disabledLinkStyles}

  /* Background styling */
  background-color: ${({ displayBackground }) =>
    displayBackground ? colors.neutral08 : 'initial'};
  position: ${({ displayBackground }) => (displayBackground ? 'relative' : 'initial')};
`;

export const NoBackground = styled.div``;
